import { useState, useMemo } from 'react';
import {
  AGENT_LIST_DOCUMENT,
  ALL_STATUS_OPTIONS,
  BUSINESS_TYPE_OPTIONS,
  isOlderThanMinutes,
} from '../utils';

type SortOrder = 'asc' | 'desc';

export const useDocumentsTaskFilters = (data: any[], country: string) => {
  const [filters, setFilters] = useState<{
    agent: string[];
    action: string[];
    business_type: string[];
  }>({
    agent: [],
    action: ['pending', 'progress'],
    business_type: ['real_estate', 'committee'],
  });

  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');

  const handleFilterChange = (
    key: 'agent' | 'action' | 'business_type',
    selected: string[]
  ) => {
    setFilters(prev => ({ ...prev, [key]: selected }));
  };

  const toggleSortOrder = () => {
    setSortOrder(prev => (prev === 'asc' ? 'desc' : 'asc'));
  };

  const filteredData = useMemo(() => {
    const filtered = data.filter(row => {
      const matchAgent =
        filters.agent.length === 0 ||
        filters.agent.includes(row.agent || 'Sin asignar');
      const matchAction =
        filters.action.length === 0 || filters.action.includes(row.action);
      const matchBusinessType =
        filters.business_type.length === 0 ||
        filters.business_type.includes(row.business_type);
      const matchDate = isOlderThanMinutes(row.date, 1);
      return matchAgent && matchAction && matchBusinessType && matchDate;
    });

    return [...filtered].sort((a, b) => {
      const aTime = new Date(a.date).getTime();
      const bTime = new Date(b.date).getTime();
      return sortOrder === 'asc' ? aTime - bTime : bTime - aTime;
    });
  }, [data, filters, sortOrder]);

  const agentOptions = useMemo(() => {
    const hardcoded = AGENT_LIST_DOCUMENT[country] || [];
    const fromData = new Set<string>();
    data.forEach(row => {
      fromData.add(row.agent || 'Sin asignar');
    });
    // Union deduped: hardcoded first (preserves intended order), then any
    // agent present in the data not already seen. 'Sin asignar' is appended
    // last when there are unassigned rows and only if not already present.
    // This prevents tasks assigned to agents outside the hardcoded list
    // from being hidden when the user filters by "todos".
    const seen = new Set<string>(hardcoded);
    const merged = [...hardcoded];
    fromData.forEach(agent => {
      if (agent === 'Sin asignar') return;
      if (!seen.has(agent)) {
        seen.add(agent);
        merged.push(agent);
      }
    });
    if (fromData.has('Sin asignar') && !seen.has('Sin asignar')) {
      merged.push('Sin asignar');
    }
    return merged.map(agent => ({ label: agent, value: agent }));
  }, [country, data]);

  const actionOptions = ALL_STATUS_OPTIONS;

  const businessTypeOptions = BUSINESS_TYPE_OPTIONS;

  return {
    filters,
    filteredData,
    agentOptions,
    actionOptions,
    businessTypeOptions,
    sortOrder,
    handleFilterChange,
    toggleSortOrder,
  };
};
